/**
 * OTA Utility Functions
 * Version comparison, storage helpers, and other utilities
 */

/**
 * Compare two semver versions
 * @param version1 - First version (e.g., "1.2.3")
 * @param version2 - Second version (e.g., "1.2.4")
 * @returns -1 if version1 < version2, 0 if equal, 1 if version1 > version2
 */
export function compareSemver(version1: string, version2: string): number {
  // Clean versions - remove build numbers and normalize
  const clean1 = version1.split('+')[0].split('-')[0]
  const clean2 = version2.split('+')[0].split('-')[0]
  
  const parts1 = clean1.split('.').map(n => parseInt(n) || 0)
  const parts2 = clean2.split('.').map(n => parseInt(n) || 0)
  
  // Pad arrays to same length
  const maxLength = Math.max(parts1.length, parts2.length)
  while (parts1.length < maxLength) parts1.push(0)
  while (parts2.length < maxLength) parts2.push(0)
  
  for (let i = 0; i < maxLength; i++) {
    if (parts1[i] < parts2[i]) return -1
    if (parts1[i] > parts2[i]) return 1
  }
  
  return 0
}

/**
 * Check if newVersion is newer than currentVersion
 * @param newVersion - Version to check
 * @param currentVersion - Current version
 * @returns true if newVersion is newer
 */
export function isNewer(newVersion: string, currentVersion: string): boolean {
  return compareSemver(newVersion, currentVersion) > 0
}

/**
 * Local storage keys for OTA data
 */
export const STORAGE_KEYS = {
  CURRENT_CHANNEL: 'ota_current_channel',
  BUNDLE_VERSION: 'ota_bundle_version',
  LAST_UPDATE_CHECK: 'ota_last_check',
  FAILED_ATTEMPTS: 'ota_failed_attempts',
  DEBUG_ENABLED: 'ota_debug_enabled',
} as const

/**
 * Get item from localStorage with fallback
 */
export function getStorageItem(key: string, fallback: string = ''): string {
  try {
    if (typeof localStorage === 'undefined') return fallback
    return localStorage.getItem(key) || fallback
  } catch {
    return fallback
  }
}

/**
 * Set item in localStorage safely
 */
export function setStorageItem(key: string, value: string): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(key, value)
    }
  } catch (error) {
    console.warn('Failed to save to localStorage:', error)
  }
}

/**
 * Remove item from localStorage safely
 */
export function removeStorageItem(key: string): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(key)
    }
  } catch (error) {
    console.warn('Failed to remove from localStorage:', error)
  }
}

/**
 * Check if we're in a development environment
 */
export function isDevelopment(): boolean {
  return process.env.NODE_ENV === 'development'
}

/**
 * Check if we're in production build
 */
export function isProduction(): boolean {
  return process.env.NODE_ENV === 'production'
}

/**
 * Safe JSON parse with fallback
 */
export function safeJsonParse<T>(json: string, fallback: T): T {
  try {
    return JSON.parse(json)
  } catch {
    return fallback
  }
}

/**
 * Create a timeout promise
 */
export function timeout(ms: number): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(`Timeout after ${ms}ms`)), ms)
  })
}

/**
 * Fetch with timeout
 */
export async function fetchWithTimeout(
  url: string, 
  options: RequestInit = {}, 
  timeoutMs: number = 10000
): Promise<Response> {
  return Promise.race([
    fetch(url, options),
    timeout(timeoutMs),
  ])
}